import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, TrendingUp, Layout } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';

const LAYOUT_OPTIONS = [
  {
    id: 'simple',
    name: 'Vista Simplificada',
    description: 'Información esencial en un diseño limpio y fácil de entender',
    icon: <Layout className="w-6 h-6 text-blue-600" />,
    features: ['Dashboard básico', 'Gráficos simples', 'Navegación intuitiva'],
    recommended: true
  },
  {
    id: 'advanced',
    name: 'Vista Avanzada',
    description: 'Panel completo con análisis detallados y múltiples visualizaciones',
    icon: <BarChart3 className="w-6 h-6 text-purple-600" />,
    features: ['Dashboard completo', 'Gráficos avanzados', 'Análisis profundo'],
    recommended: false
  }
];

export const DashboardStep: React.FC = () => {
  const { userPreferences, saveUserPreferences } = useOnboarding();
  const [selectedLayout, setSelectedLayout] = useState<'simple' | 'advanced'>('simple');

  // Cargar configuración desde las preferencias
  useEffect(() => {
    if (userPreferences?.dashboardLayout) {
      setSelectedLayout(userPreferences.dashboardLayout);
    }
  }, [userPreferences]);

  const handleLayoutSelect = (layout: 'simple' | 'advanced') => {
    setSelectedLayout(layout);
    saveUserPreferences({ dashboardLayout: layout });
  };

  const selectedOption = LAYOUT_OPTIONS.find(option => option.id === selectedLayout);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          Personaliza tu dashboard
        </h4>
        <p className="text-gray-600">
          Elige cómo quieres ver tu información financiera
        </p>
      </div>

      {/* Opciones de layout */}
      <div className="space-y-4">
        {LAYOUT_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() => handleLayoutSelect(option.id as 'simple' | 'advanced')}
            className={`w-full p-6 text-left border rounded-lg transition-all ${
              selectedLayout === option.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                {option.icon}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h5 className="font-semibold text-gray-900">
                    {option.name}
                  </h5>
                  {option.recommended && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Recomendado
                    </span>
                  )}
                </div>
                
                <p className="text-gray-600 mb-3">
                  {option.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {option.features.map((feature, index) => (
                    <span
                      key={index}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Indicador de selección */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedLayout === option.id
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300'
              }`}>
                {selectedLayout === option.id && (
                  <div className="w-2 h-2 bg-white rounded-full" />
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Vista previa del layout seleccionado */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h5 className="font-medium text-gray-900 mb-4">
          Vista previa: {selectedOption?.name}
        </h5>
        
        {selectedLayout === 'simple' ? (
          <div className="space-y-4">
            {/* Simulación de vista simple */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-700">Gastos del Mes</span>
                <span className="text-2xl font-bold text-blue-600">€1,234</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '60%' }} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                <PieChart className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <span className="text-sm text-gray-600">Categorías</span>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <span className="text-sm text-gray-600">Tendencias</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Simulación de vista avanzada */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border border-gray-200 text-center">
                <span className="text-lg font-bold text-green-600">€500</span>
                <div className="text-xs text-gray-600">Ingresos</div>
              </div>
              <div className="bg-white p-3 rounded border border-gray-200 text-center">
                <span className="text-lg font-bold text-red-600">€1,234</span>
                <div className="text-xs text-gray-600">Gastos</div>
              </div>
              <div className="bg-white p-3 rounded border border-gray-200 text-center">
                <span className="text-lg font-bold text-blue-600">€266</span>
                <div className="text-xs text-gray-600">Balance</div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Análisis Detallado</span>
                <BarChart3 className="w-4 h-4 text-gray-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Alimentación</span>
                  <span className="font-medium">€400</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Transporte</span>
                  <span className="font-medium">€200</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Entretenimiento</span>
                  <span className="font-medium">€150</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h6 className="font-medium text-blue-900 mb-2">
          ¿No estás seguro?
        </h6>
        <p className="text-sm text-blue-800 mb-2">
          Recomendamos comenzar con la vista simplificada. Es más fácil de usar 
          y siempre puedes cambiar a la vista avanzada más tarde.
        </p>
        <p className="text-xs text-blue-700">
          Puedes cambiar este ajuste en cualquier momento desde la configuración.
        </p>
      </div>

      {/* Resumen de la selección */}
      <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
        <h6 className="font-medium text-green-900 mb-1">
          Has elegido: {selectedOption?.name}
        </h6>
        <p className="text-sm text-green-700">
          Tu dashboard se configurará con esta vista cuando completes el proceso
        </p>
      </div>
    </div>
  );
};